<template>
  <div
    ref="selectGroup"
    class="select_group"
    :class="{
      'select--fullHeight': fullHeight,
    }"
  >
    <vSelect
      v-bind="$attrs"
      :options="list"
      :modelValue="modelValue"
      @update:modelValue="update"
      @open="onDropdownOpen"
      @close="onDropdownClose"
      class="select"
      :class="{
        'is-invalid': invalid,
        secondary: !isPrimary,
        'select--placement-top': effectivePlacement === 'top',
      }"
    >
      <template #open-indicator="{ attributes }">
        <AppIcon icon="chevron-down" :size="12" v-bind="attributes" />
      </template>

      <template #option="option">
        <div class="select__option-with-image" v-if="hasImage(option)">
          <div
            v-if="getImageUrl(option)"
            class="select__option-image"
            :style="{
              backgroundImage: `url(${getImageUrl(option)})`,
            }"
            role="img"
            :aria-label="getOptionLabel(option)"
          />
          <AppIcon
            v-else-if="getIconName(option)"
            :icon="getIconName(option)"
            :size="imageSize"
            :color="option.iconColor"
            class="select__option-icon"
          />
          <span class="select__option-label">{{ getOptionLabel(option) }}</span>
        </div>
        <span v-else>{{ option.label || option }}</span>
      </template>

      <template #selected-option="option">
        <div class="select__selected-with-image" v-if="hasImage(option)">
          <div
            v-if="getImageUrl(option)"
            class="select__selected-image"
            :style="{
              backgroundImage: `url(${getImageUrl(option)})`,
            }"
            role="img"
            :aria-label="getOptionLabel(option)"
          />
          <AppIcon
            v-else-if="getIconName(option)"
            :icon="getIconName(option)"
            :size="imageSize"
            :color="option.iconColor"
            class="select__selected-icon"
          />
          <span class="select__selected-label">{{
            getOptionLabel(option)
          }}</span>
        </div>
        <span v-else>{{ option.label || option }}</span>
      </template>
    </vSelect>
    <p class="select_error" v-if="invalid && error">
      <span>{{ error }}</span>
    </p>
  </div>
</template>

<script>
import vSelect from 'vue-select';

export default {
  props: {
    view: {
      type: String,
      default: 'primary',
    },
    fullHeight: {
      type: Boolean,
      default: false,
    },
    list: {
      type: Array,
    },
    modelValue: {
      type: null,
    },
    invalid: {
      type: null,
    },
    error: {
      type: String,
    },
    placement: {
      type: String,
      default: 'bottom',
      validator: (value) => ['bottom', 'top', 'auto'].includes(value),
    },
    showImages: {
      type: Boolean,
      default: false,
    },
    imageKey: {
      type: String,
      default: 'imageUrl',
    },
    iconKey: {
      type: String,
      default: 'icon',
    },
    imageSize: {
      type: Number,
      default: 38,
    },
  },
  data() {
    return {
      resolvedPlacement: this.placement === 'auto' ? 'bottom' : this.placement,
    };
  },
  computed: {
    isPrimary() {
      return this.view === 'primary';
    },
    effectivePlacement() {
      if (this.placement === 'auto') {
        return this.resolvedPlacement;
      }
      return this.placement;
    },
  },
  watch: {
    placement(newVal) {
      if (newVal !== 'auto') {
        this.removeScrollListener();
        this.resolvedPlacement = newVal;
      }
    },
  },
  beforeUnmount() {
    this.removeScrollListener();
  },
  methods: {
    update($event) {
      this.$emit('update:modelValue', $event);
    },
    getImageUrl(option) {
      if (!option || typeof option !== 'object') return null;
      return option[this.imageKey] || option.img;
    },
    getIconName(option) {
      if (!option || typeof option !== 'object') return null;
      return option[this.iconKey];
    },
    getOptionLabel(option) {
      if (typeof option === 'string' || typeof option === 'number') {
        return option;
      }
      return option?.label || option?.name || option;
    },
    hasImage(option) {
      return (
        this.showImages &&
        (this.getImageUrl(option) || this.getIconName(option))
      );
    },
    onDropdownOpen() {
      if (this.placement !== 'auto') return;
      this.$nextTick(() => {
        this.resolvedPlacement = this.calculatePlacement();
      });
      this.addScrollListener();
    },
    onDropdownClose() {
      if (this.placement !== 'auto') return;
      this.removeScrollListener();
    },
    calculatePlacement() {
      const MARGIN = 8;
      const el = this.$refs.selectGroup;
      if (!el) return 'bottom';
      const toggle = el.querySelector('.vs__dropdown-toggle');
      if (!toggle) return 'bottom';
      const dropdown = el.querySelector('.vs__dropdown-menu');
      const dropdownHeight = dropdown ? dropdown.scrollHeight : 320;
      const rect = toggle.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      if (spaceBelow >= dropdownHeight + MARGIN) return 'bottom';
      if (spaceAbove > spaceBelow) return 'top';
      return 'bottom';
    },
    addScrollListener() {
      this._scrollHandler = () => {
        if (this.placement !== 'auto') return;
        this.resolvedPlacement = this.calculatePlacement();
      };
      window.addEventListener('scroll', this._scrollHandler, {
        passive: true,
        capture: true,
      });
    },
    removeScrollListener() {
      if (this._scrollHandler) {
        window.removeEventListener('scroll', this._scrollHandler, {
          capture: true,
        });
        this._scrollHandler = null;
      }
    },
  },
  components: {
    vSelect,
  },
};
</script>

<style lang="scss" scoped>
.select {
  &.is-invalid {
    --border-color: var(--color-error);
    animation: headShake 1s ease;

    :deep(.vs__dropdown-toggle) {
      border-color: var(--color-error);
    }
  }
  &--fullHeight {
    .select {
      height: 100%;
      display: flex;
      align-items: center;
    }
  }

  :deep(.vs__dropdown-menu) {
    max-height: 320px;
    overflow-y: auto;
  }

  &.secondary {
    background: #fff;
    backdrop-filter: blur(21.45px);
    color: #25262c;
    z-index: 1;

    :deep(.vs__dropdown-toggle) {
      border: none;
    }
    :deep(.vs__search) {
      color: #25262c;
      &::placeholder {
        color: #25262c;
      }
    }

    :deep(.vs__dropdown-option--highlight) {
      background: #f2f2f2;
      color: #25262c;
    }

    :deep(.vs__dropdown-menu) {
      left: 0;
      right: unset;
      width: 100%;
      border: none;
      box-shadow: none;
      background: #fff;
      color: #25262c;
      transition: unset;
    }
  }

  // Top placement variant
  &.select--placement-top {
    // Position dropdown above toggle
    :deep(.vs__dropdown-menu) {
      top: auto;
      bottom: 100%;
      border-radius: 15px 15px 0 0;
      z-index: 1001;
    }

    // Adjust toggle border radius when open
    &.vs--open {
      :deep(.vs__dropdown-toggle) {
        border-bottom-color: inherit;
        border-bottom-left-radius: 15px;
        border-bottom-right-radius: 15px;
        border-top-color: transparent;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }
    }

    // Secondary variant adjustments
    &.secondary {
      :deep(.vs__dropdown-menu) {
        z-index: 2;
      }

      &.vs--open {
        :deep(.vs__dropdown-toggle) {
          border-top: none;
        }
      }
    }
  }

  // Image support styles
  .select__option-with-image,
  .select__selected-with-image {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .select__option-image,
  .select__selected-image {
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    border-radius: 8px;
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
    border: 2px solid #e0e0e0;
  }

  .select__option-icon,
  .select__selected-icon {
    flex-shrink: 0;
  }

  .select__option-label,
  .select__selected-label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // Adjust for secondary variant
  &.secondary {
    .select__option-image,
    .select__selected-image {
      border-color: #cfcfcf;
    }
  }

  // Vue-select overrides for better image alignment
  :deep(.vs__dropdown-option) {
    padding: 8px 12px;
  }

  :deep(.vs__dropdown-option--highlight) {
    .select__option-image {
      border-color: #5897fb;
    }
  }

  :deep(.vs__selected) {
    display: flex;
    align-items: center;
    padding: 4px 8px;
  }
}
.select_error {
  color: var(--color-error);
  font-weight: 600;
  padding: 0.25em 1em;

  span {
    font-size: 12px;
  }
}
</style>
